import {
  convertPercentageToMarks,
  getLocalStorage,
  SelectMenu,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AttemptStats,
  LiveQuizSession,
  SessionStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { Timestamp } from '@bufbuild/protobuf';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuizContext } from '../../../app/Context/LiveQuizContextProvider';
import {
  LIVE_QUIZ_LOBBY,
  LIVE_QUIZ_PLAY,
} from '../../../routeHandling/RoutesNomenclature';
import { ConnectedClassStartBox } from '../../ManageHomework/ClickerAssessmentOngoingHw/components/ConnectedClassStartBox';
import { isTimeLeft } from '../../ManageHomework/manageHWutils';
import { el } from 'date-fns/locale';
import { setLiveQuizSession } from '../reducer/liveQuiz.slice';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import {
  setCurrentAssessmentClassStats,
  setSelectedLiveQuizAttempt,
} from '../../ManageHomework/reducer/manageHomework.slice';
import {
  IStatusObj,
  setClickerAssessmentInfo,
} from '../../Assessment/ClickerAssessment/reducer/clickerAssessment.slice';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';

// Slots into HwDetails at the same position Clicker's ConnectedClassStartBox
// occupies in CAOngoingHwLeftPanel — top of the left column, right below the
// header, above the deadline box — reusing that exact shared component
// (not a lookalike) so a live quiz's Manage view reads as the same kind of
// page as Clicker's, just embedded in the shared HwDetails/StudentSection
// tree instead of a dedicated page.
interface IProps {
  assessment: AssessmentTask;
}
export const LiveQuizStartTrigger = (props: IProps) => {
  const { assessment } = props;
  const navigate = useNavigate();
  const { createLiveQuizSession, fetchPreviousSessions } = useLiveQuizContext();
  const teacher_id = getLocalStorage('userId');
  const [starting, setStarting] = useState(false);
  const [previousSessions, setPreviousSessions] = useState<LiveQuizSession[]>(
    []
  );
  const dispatch = useAppDispatch();
  const { LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler } =
    useCommonServiceClientContext();
  const { live_quiz_session } = useAppSelector((state) => state.liveQuiz);
  // Same field + same utility as ClickerAssessmentManage's own
  // isTimeLeft(assessmentTargetDate) check — this is the belt-and-suspenders
  // fallback for the brief window before OngoingHomework's own effect (same
  // check) redirects away to HOMEWORK_COMPLETED once ended.
  const hasEnded = assessment.assessmentTargetDate
    ? !isTimeLeft(new Timestamp(assessment.assessmentTargetDate))
    : false;

  useEffect(() => {
    setPreviousSessions([]);
    if (!teacher_id || !assessment.sectionId || !assessment.assessmentId)
      return;
    let cancelled = false;
    fetchPreviousSessions(
      BigInt(teacher_id),
      assessment.sectionId,
      assessment.assessmentId
    ).then((s) => {
      if (!cancelled) setPreviousSessions(s);
    });
    return () => {
      cancelled = true;
    };
  }, [assessment.assessmentId, assessment.sectionId]);

  // Like Clicker's "Attempt N" selector on its Ended view — a teacher can
  // start a brand new session for the same assessment/section as many times
  // as they like (createLiveQuizSession only ever resumes a still-PAUSED
  // session; anything COMPLETED just gets a fresh one), so past completed
  // attempts need their own way to be looked back at instead of only ever
  // being reachable through the just-ended flow. Oldest-first numbering,
  // newest attempt listed first (most likely one a teacher wants to revisit).
  const completedAttempts = [...previousSessions]
    .filter(
      (s) => s.sessionStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
    )
    .sort(
      (a, b) =>
        Number(a.sessionStartTime?.seconds ?? 0) -
        Number(b.sessionStartTime?.seconds ?? 0)
    );
  const latestAttempt = [...previousSessions].sort(
    (a, b) =>
      Number(b.sessionStartTime?.seconds ?? 0) -
      Number(a.sessionStartTime?.seconds ?? 0)
  )[0];
  const attemptOptionList = completedAttempts
    .map((session, index) => ({
      name: `Attempt ${index + 1}`,
      id: index + 1,
      session,
    }))
    .reverse();

  // Clicker's own dropdown always displays the most recent attempt as its
  // current value (not an empty placeholder) — matches attemptOptionList's
  // own newest-first order. Purely a display of how many attempts exist so
  // far (no dedicated per-attempt results view to drill into here), so
  // selecting a different entry just changes which label is shown.
  // const [selectedAttemptName, setSelectedAttemptName] = useState<
  //   string | undefined
  // >();
  // const displayedAttemptLabel =
  //   selectedAttemptName ?? attemptOptionList[0]?.name ?? '';
  // const defaultAttemptNumber = attemptOptionList[0]?.id;

  // useEffect(() => {
  //   if (
  //     selectedAttemptName === undefined &&
  //     defaultAttemptNumber !== undefined
  //   ) {
  //     dispatch(setSelectedLiveQuizAttempt(defaultAttemptNumber));
  //   }
  // }, [defaultAttemptNumber, dispatch, selectedAttemptName]);
  const selectedAttempt = useAppSelector(
    (state) => state.manageHomework.selected_live_quiz_attempt
  );

  // Fall back to newest if nothing is selected or selection is no longer valid
  const effectiveAttempt =
    attemptOptionList.find((o) => o.id === selectedAttempt)?.id ??
    attemptOptionList[0]?.id;

  const displayedAttemptLabel =
    attemptOptionList.find((o) => o.id === effectiveAttempt)?.name ?? '';

  // Keep redux in sync with what is actually shown (also clears it when empty)
  useEffect(() => {
    if (effectiveAttempt !== selectedAttempt) {
      dispatch(setSelectedLiveQuizAttempt(effectiveAttempt));
    }
  }, [effectiveAttempt, selectedAttempt, dispatch]);

  // Fetch stats for whatever is displayed, ignoring stale responses
  useEffect(() => {
    if (effectiveAttempt === undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const response =
          await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getAssessmentClassStats(
            {
              teacherId: BigInt(teacher_id),
              assessmentId: assessment.assessmentId,
              attemptNumber: effectiveAttempt,
            }
          );
        if (cancelled) return;
        if (response.data)
          dispatch(setCurrentAssessmentClassStats(response.data));
        else {
          dispatch(
            setToastInfo({
              label: `Data for Attempt ${effectiveAttempt} could not be fetched.`,
              variant: 'error',
              open: true,
            })
          );
        }
      } catch {
        if (!cancelled) {
          dispatch(
            setToastInfo({
              label: `Data for Attempt ${effectiveAttempt} could not be fetched.`,
              variant: 'error',
              open: true,
            })
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveAttempt, assessment.assessmentId]);

  const handleStart = async () => {
    if (!teacher_id || !assessment.assessmentId || !assessment.sectionId)
      return;
    setStarting(true);
    const res = await createLiveQuizSession({
      teacherId: BigInt(teacher_id),
      assessmentId: assessment.assessmentId,
      schoolClassSectionId: assessment.sectionId,
    });
    setStarting(false);
    if (res?.liveQuizSession) {
      const currentQuestionSessionId =
        res.liveQuizSession.currentQuestionSessionId;
      if (currentQuestionSessionId) {
        //When user went back after starting a question session
        navigate(LIVE_QUIZ_PLAY);
        return;
      }
      const questionSessions = res.questionSessions;
      const inverseSortedSessions = [...questionSessions].sort(
        (a, b) => b.sequence - a.sequence
      );
      const latestCompletedSession = inverseSortedSessions.find(
        (s) => s.sessionStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
      );
      console.log('Session latestCompletedSession', latestCompletedSession);
      if (!latestCompletedSession) {
        //No previous completed session exists, so this is the first time the teacher is starting the live quiz. Navigate to the lobby to start the first question session.
        navigate(LIVE_QUIZ_LOBBY);
        return;
      } else {
        const nextSequence = latestCompletedSession.sequence + 1;
        const nextSession = questionSessions.find(
          (s) => s.sequence === nextSequence
        );
        console.log('Session nextSession', nextSequence, nextSession);
        if (nextSequence > questionSessions.length) {
          dispatch(
            setToastInfo({
              label: 'This live quiz has already been completed.',
              variant: 'error',
              open: true,
            })
          );
          // Every question has already been ended/revealed, but the teacher
          // never clicked "End Quiz" — they navigated away instead (Play's
          // own pause-on-leave paused the session rather than ending it).
          // That's not "already completed", it's just unfinished — land
          // back on Play, which resolves to the last (completed) question
          // on its own, so End Quiz is reachable again instead of dead-ending
          // here.
          // navigate(LIVE_QUIZ_PLAY);
          return;
        }
        //If the next session exists, navigate to the play page with the autoStartQuestionSessionId state.
        // If it doesn't exist, navigate to the lobby to start a new question session.
        navigate(LIVE_QUIZ_PLAY, {
          state: nextSession
            ? {
                autoStartQuestionSessionId:
                  nextSession.liveQuizQuestionSessionId,
              }
            : undefined,
        });
      }
      // Resuming a session that already had a question mid-flight when
      // paused goes straight back into Play instead of the Lobby.
      // navigate(
      //   res.liveQuizSession.currentQuestionSessionId
      //     ? LIVE_QUIZ_PLAY
      //     : LIVE_QUIZ_LOBBY
      // );
    } else {
      dispatch(
        setToastInfo({
          label:
            'Could not start the live quiz — check that every question is MCQ or True/False.',
          variant: 'error',
          open: true,
        })
      );
    }
  };
  const getAssessmentClassStats = async (attemptNumber?: number) => {
    if (!teacher_id || !assessment.assessmentId) {
      return;
    }

    try {
      const response =
        await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getAssessmentClassStats(
          {
            teacherId: BigInt(teacher_id),
            assessmentId: assessment.assessmentId,
            attemptNumber: attemptNumber,
          }
        );

      if (response.data) {
        dispatch(setCurrentAssessmentClassStats(response.data));
      } else {
        dispatch(
          setToastInfo({
            label: `Data for Attempt ${
              attemptNumber ?? ''
            } could not be fetched.`,
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (error) {
      console.error('Error fetching assessment class stats:', error);

      dispatch(
        setToastInfo({
          label: `Data for Attempt ${
            attemptNumber ?? ''
          } could not be fetched.`,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <ConnectedClassStartBox
        isOnline
        label="Geneo Live Quiz"
        disabled={starting}
        // ConnectedClassStartBox's own default width pins to a fixed
        // pxTovW(602) at the lg breakpoint instead of filling its parent —
        // fine for Clicker's own page, but here it sits above HwDetails'
        // other left-column boxes (stats row, deadline box), which are all
        // plain width:'100%' and were rendering a few px narrower as a
        // result. Forcing 100% here lines every box up on the same edges.
        rootStyles={{ width: '100%' }}
        primaryButton={
          hasEnded
            ? undefined
            : {
                text: starting
                  ? 'Starting…'
                  : latestAttempt?.sessionStatus ===
                    SessionStatusEnum.SESSION_STATUS_PAUSED
                  ? 'RESUME'
                  : 'START',
                clickHandler: handleStart,
              }
        }
      />

      {/* Below the Start box, same position/style as Clicker's own attempt
          selector on its ongoing-homework view — SelectMenu's default look
          (light-blue box, border, chevron) already matches it as-is. */}
      {attemptOptionList.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <SelectMenu
            placeholder="Attempt"
            value={displayedAttemptLabel}
            // onChange={setSelectedAttemptName}
            // onChange={(value: string) => {
            //   setSelectedAttemptName(value);
            //   const attemptNumber = Number(value.split(' ')[1]);
            //   const selectedOption = attemptOptionList.find(
            //     (item) => item.id === attemptNumber
            //   );

            //   if (!selectedOption) {
            //     return;
            //   }
            //   dispatch(setSelectedLiveQuizAttempt(attemptNumber));
            //   getAssessmentClassStats(attemptNumber);
            // }}
            onChange={(value: string) => {
              const opt = attemptOptionList.find((o) => o.name === value);
              if (opt) dispatch(setSelectedLiveQuizAttempt(opt.id));
            }}
            optionList={attemptOptionList}
            rootStyles={{ width: '100%' }}
            selectedBold
          />
        </Box>
      )}
      {hasEnded && (
        <Typography
          variant="smallText"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          This assessment's deadline has passed — it can no longer be started.
        </Typography>
      )}
    </Box>
  );
};

export default LiveQuizStartTrigger;
